import SVG from 'react-inlinesvg'
import { twMerge } from 'tailwind-merge'

import { Button } from '@/components/button'
import { Skeleton } from '@/components/skeleton'
import { Banners } from '@/models/banner'

interface BannerProps {
  banner: Banners
}

export function Banner({ banner }: BannerProps) {
  function handleProcessorSvg(code: string) {
    let svgCode = code.replace(/<path/g, '<path fill="currentColor"')

    const hasViewBox = /viewBox=".*?"/.test(svgCode)
    if (hasViewBox) return svgCode

    const width = svgCode.match(/width="(\d+)"/)?.[1]
    const height = svgCode.match(/height="(\d+)"/)?.[1]

    if (width && height) {
      svgCode = svgCode.replace(
        /<svg([^>]+)>/,
        `<svg$1 viewBox="0 0 ${width} ${height}">`,
      )
    }

    return svgCode
  }

  return (
    <section className="relative size-full">
      {/* Imagem de fundo */}
      <img
        src={banner.file_url}
        alt="photo from the banner"
        className="absolute inset-0 -z-10 size-full object-cover"
      />
      {/* Gradient overlay */}
      <div className="banner-gradient absolute inset-0 -z-5" />

      <div className="flex size-full flex-col-reverse justify-center px-5 lg:relative lg:flex-row lg:items-center lg:justify-between">
        <div className="relative flex w-full items-center justify-between gap-5 px-5 py-5 lg:w-1/2 lg:items-start lg:justify-start lg:px-20 lg:py-20 lg:pr-0 xl:px-40 xl:pr-0">
          <div className="flex size-full w-full max-w-xs flex-col items-center justify-center gap-5 min-[425px]:px-4 sm:px-14 lg:w-auto lg:max-w-3xl lg:items-start lg:p-0 2xl:max-w-5xl">
            <div className="flex max-w-full items-center justify-center gap-2 rounded-lg bg-bios-100 p-2 text-bios-secondary">
              {banner.tag_icon_url && (
                <div>
                  <SVG
                    className="size-6"
                    src={banner.tag_icon_url}
                    loader={<Skeleton className="size-6 rounded-full" />}
                    cacheRequests
                    uniquifyIDs
                    preProcessor={handleProcessorSvg}
                  />
                </div>
              )}
              <span className="line-clamp-2 wrap-break-word text-center font-bold">
                {banner.tag}
              </span>
            </div>

            <div className="flex max-w-full flex-col items-center gap-4 rounded-lg p-2 lg:items-start">
              <h1 className="text-shadow-lg card-title text-shadow-title line-clamp-6 max-w-full wrap-break-word text-center text-2xl text-white lg:line-clamp-5 lg:text-start lg:text-4xl xl:text-5xl 2xl:line-clamp-4">
                {banner.title}
              </h1>

              <div className="flex w-full flex-1 items-center text-center lg:text-start">
                <p className="text-shadow-lg my-auto max-w-full wrap-break-word rounded-lg text-base text-white lg:text-lg 2xl:text-2xl">
                  {banner.description}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              className={twMerge(
                'border border-bios-primary bg-transparent font-normal uppercase text-white',
                banner.site_url ? 'visible' : 'invisible',
              )}
              asChild
            >
              <a
                href={banner.site_url}
                rel="noopener noreferrer"
                target="_blank"
                aria-label="Saiba mais sobre o banner"
              >
                Saiba mais
              </a>
            </Button>
          </div>
        </div>
        {/* Logo do banner */}
        {banner.logo_url && (
          <div className="mx-auto max-h-56 min-h-24 w-1/4 min-w-24 max-w-56 lg:mr-20 lg:flex lg:size-full 2xl:mr-24">
            <img
              className="size-full select-none object-contain"
              alt="logo"
              src={banner.logo_url}
            />
          </div>
        )}
      </div>
    </section>
  )
}
